/**
Project: Voice Panel (c)
Title: Setup 
Description: Component to setup the voice regions by create PSTN, Voice routes, Routing policy.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import classNames from "classnames/bind";
import { Text, Button, Flex, ArrowRightIcon } from "@fluentui/react-northstar";
import { Grid, Row, Col } from "react-flexbox-grid";
import IntlUtil from "../../../../core/helpers/intl-util";
import PageUtil from "../../../../core/helpers/page-util";
import ResponseUtil from "../../../../core/helpers/response-util";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import {
  manageError,
  openConsentPopup,
} from "../../../../core/actions/common-actions";
import {
  setVoiceSetupProgressPage,
  addPstnUsage,
  addVoiceRoute,
  addRoutingPolicy,
  addVoiceDomains,
} from "../actions/voice-setup-actions";
import {
  setSubscriberIdentity,
  setSubscriberAccount,
  setSubscriberMenus,
} from "../../common/actions/subscriber-actions";
import { AppConfigProps } from "../../../../core/settings/app-config";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import PageExitGuard from "../../common/helpers/page-exit-guard";
import SetupItem from "./setup-item";

class Setup extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_voicesetup = "vpa_voicesetup";

  constructor(props) {
    super(props);
    this.state = {
      pstnStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.NOT_STARTED,
      routeStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.NOT_STARTED,
      policyStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.NOT_STARTED,
      accountStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.NOT_STARTED,
      domainStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.NOT_STARTED,
      progressMessage: null,
      errorMessage: null,
      errorDetails: null,
      isError: false,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
    await this.checkVoiceRegions();
    if (
      this.props.voiceSetupProgressVoiceRegions &&
      this.props.voiceSetupProgressVoiceRegions.length > 0
    ) {
      const voiceRegions = this.props.voiceSetupProgressVoiceRegions;
      let regions = voiceRegions.map((reg) => {
        return { regionName: reg, status: null };
      });
      await this.setStateAsync({ regions: regions });
    }
    await this.loadPageData();
  }
  componentWillUnmount() {
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.isRoutingEnablePage &&
      (prevState.domainStatus !== this.state.domainStatus ||
        prevState.pstnStatus !== this.state.pstnStatus ||
        prevState.routeStatus !== this.state.routeStatus ||
        prevState.policyStatus !== this.state.policyStatus)
    ) {
      if (
        this.state.domainStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.IN_PROGRESS ||
        this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.IN_PROGRESS ||
        this.state.routeStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.IN_PROGRESS ||
        this.state.policyStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.IN_PROGRESS
      ) {
        this.props.setUpInProgress();
      }
      if (
        this.state.domainStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE ||
        this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE ||
        this.state.routeStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE ||
        this.state.policyStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE
      ) {
        this.props.setUpFailure();
      }
      if (
        this.state.policyStatus ===
        VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS
      ) {
        this.props.setUpCompleted();
      }
    }
  }

  checkVoiceRegions = async () => {
    if (
      this.props.voiceSetupProgressVoiceRegions === null ||
      this.props.voiceSetupProgressVoiceRegions.length === 0
    ) {
      await this.props.setVoiceSetupProgressPage(
        VoiceAdminConstants.VOICE_SETUP.PROGRESS.WELCOME
      );
    }
  };

  loadPageData = async () => {
    if (
      this.props.voiceSetupProgressVoiceRegions &&
      this.props.voiceSetupProgressVoiceRegions.length > 0
    ) {
      if (
        this.state.domainStatus !==
        VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS
      ) {
        for (let reg of this.state.regions) {
          if (
            !reg.status ||
            reg.status === VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE
          ) {
            await this.createDomain(reg.regionName);
          }

          if (
            reg.status !== VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS
          )
            if (
              this.state.domainStatus ===
              VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE
            ) {
              let regions = this.state.regions.map((re) => {
                if (re.regionName === reg.regionName)
                  re.status =
                    VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE;
                return re;
              });
              await this.setStateAsync({ regions: regions });
              break;
            } else {
              let regions = this.state.regions.map((re) => {
                if (re.regionName === reg.regionName)
                  re.status =
                    VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS;
                return re;
              });
              await this.setStateAsync({ regions: regions });
            }
        }
        if (
          this.state.regions?.every(
            (re) =>
              re.status === VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS
          )
        ) {
          await this.setStateAsync({
            domainStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS,
          });
        }
      }
      if (
        this.state.domainStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.state.pstnStatus !==
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS
      ) {
        await this.createPstnUsage();
      }
      if (
        this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.state.routeStatus !==
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS
      ) {
        await this.createVoiceRoute();
      }
      if (
        this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.state.routeStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.state.policyStatus !==
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS
      ) {
        await this.createRoutingPolicy();
      }

      if (
        this.state.domainStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.state.routeStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.state.policyStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        !this.props.isRoutingEnablePage
      ) {
        await this.updateSubscriberAccount();
      }

      if (
        this.state.domainStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.state.routeStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.state.policyStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS &&
        this.props.isRoutingEnablePage
      ) {
        await this.setStateAsync({
          progressMessage: IntlUtil.getText(
            this._intl_ns_vpa_voicesetup,
            "notification.info.completeSetUpProcess"
          ),
        });
      }
    }
  };

  createDomain = async (reg) => {
    this.setStateAsync({
      progressMessage: null,
      errorMessage: null,
      errorDetails: null,
    });
    const voiceRegions = [reg];
    await this.setStateAsync({
      domainStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.IN_PROGRESS,
      progressMessage:
        IntlUtil.getText(
          this._intl_ns_vpa_voicesetup,
          "notification.info.domainCreate"
        ) +
        " " +
        voiceRegions.join(", "),
    });

    const data = { regions: voiceRegions };
    await addVoiceDomains(data, this.cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data
        ) {
          await this.setStateAsync({
            progressMessage: res.data.message,
          });
        } else {
          await this.setStateAsync({
            domainStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
            errorMessage: IntlUtil.getText(
              this._intl_ns_vpa_voicesetup,
              "notification.error.domainCreateFailed"
            ),
          });
        }
      })
      .catch(async (err) => {
        const errorStatus = ResponseUtil.getStatus(err);
        if (
          errorStatus &&
          errorStatus === AppConfigProps.httpStatusCode.badRequest
        ) {
          const primeError = ResponseUtil.getPrimeError(err);
          if (
            primeError &&
            primeError.code ===
              VoiceAdminConstants.SERVER_ERROR_CODE.DOMAIN_CREATE_FAILED
          ) {
            await this.setStateAsync({
              errorDetails: primeError.message,
            });
          }
          await this.setStateAsync({
            domainStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
            errorMessage: IntlUtil.getText(
              this._intl_ns_vpa_voicesetup,
              "notification.error.domainCreateFailed"
            ),
          });
        } else {
          await this.setState({ isError: true });
          await manageError(err, this.props.history);
        }
      });
  };

  createPstnUsage = async () => {
    this.setStateAsync({
      progressMessage: null,
      errorMessage: null,
      errorDetails: null,
    });
    const voiceRegions = this.props.voiceSetupProgressVoiceRegions;
    this.setStateAsync({
      pstnStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.IN_PROGRESS,
      progressMessage:
        IntlUtil.getText(
          this._intl_ns_vpa_voicesetup,
          "notification.info.pstnUsageCreate"
        ) +
        " " +
        voiceRegions.join(", "),
    });
    const data = { regions: voiceRegions };
    await addPstnUsage(data, this.cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data
        ) {
          this.setStateAsync({
            pstnStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS,
            progressMessage: res.data.message,
          });
        } else {
          this.setStateAsync({
            pstnStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
            errorMessage: IntlUtil.getText(
              this._intl_ns_vpa_voicesetup,
              "notification.error.pstnUsageCreateFailed"
            ),
          });
        }
      })
      .catch(async (err) => {
        const errorStatus = ResponseUtil.getStatus(err);
        if (
          errorStatus &&
          errorStatus === AppConfigProps.httpStatusCode.badRequest
        ) {
          const primeError = ResponseUtil.getPrimeError(err);
          if (
            primeError &&
            primeError.code ===
              VoiceAdminConstants.SERVER_ERROR_CODE.PSTN_USAGE_CREATE_FAILED
          ) {
            await this.setStateAsync({
              errorDetails: primeError.message,
            });
          }
          this.setStateAsync({
            pstnStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
            errorMessage: IntlUtil.getText(
              this._intl_ns_vpa_voicesetup,
              "notification.error.pstnUsageCreateFailed"
            ),
          });
        } else {
          await this.setState({ isError: true });
          await manageError(err, this.props.history);
        }
      });
  };

  createVoiceRoute = async () => {
    this.setStateAsync({
      progressMessage: null,
      errorMessage: null,
      errorDetails: null,
    });
    const voiceRegions = this.props.voiceSetupProgressVoiceRegions;
    this.setStateAsync({
      routeStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.IN_PROGRESS,
      progressMessage:
        IntlUtil.getText(
          this._intl_ns_vpa_voicesetup,
          "notification.info.voiceRouteCreate"
        ) +
        " " +
        voiceRegions.join(", "),
    });
    const data = { regions: voiceRegions };
    return new Promise(async (resolve, reject) => {
      setTimeout(async () => {
        await addVoiceRoute(data, this.cancelToken)
          .then(async (res) => {
            if (
              res &&
              res.status === AppConfigProps.httpStatusCode.ok &&
              res.data
            ) {
              this.setStateAsync({
                routeStatus:
                  VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS,
                progressMessage: res.data.message,
              });
            } else {
              this.setStateAsync({
                routeStatus:
                  VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
                errorMessage: IntlUtil.getText(
                  this._intl_ns_vpa_voicesetup,
                  "notification.error.voiceRouteCreateFailed"
                ),
              });
            }
          })
          .catch(async (err) => {
            const errorStatus = ResponseUtil.getStatus(err);
            if (
              errorStatus &&
              errorStatus === AppConfigProps.httpStatusCode.badRequest
            ) {
              const primeError = ResponseUtil.getPrimeError(err);
              if (
                primeError &&
                primeError.code ===
                  VoiceAdminConstants.SERVER_ERROR_CODE
                    .VOICE_ROUTES_CREATE_FAILED
              ) {
                await this.setStateAsync({
                  errorDetails: primeError.message,
                });
              }
              this.setStateAsync({
                routeStatus:
                  VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
                errorMessage: IntlUtil.getText(
                  this._intl_ns_vpa_voicesetup,
                  "notification.error.voiceRouteCreateFailed"
                ),
              });
            } else {
              await this.setState({ isError: true });
              await manageError(err, this.props.history);
            }
          });
        resolve();
      }, 5000);
    });
  };

  createRoutingPolicy = async () => {
    this.setStateAsync({
      progressMessage: null,
      errorMessage: null,
      errorDetails: null,
    });
    const voiceRegions = this.props.voiceSetupProgressVoiceRegions;
    this.setStateAsync({
      policyStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.IN_PROGRESS,
      progressMessage:
        IntlUtil.getText(
          this._intl_ns_vpa_voicesetup,
          "notification.info.routePolicyCreate"
        ) +
        " " +
        voiceRegions.join(", "),
    });

    const data = { regions: voiceRegions };
    await addRoutingPolicy(data, this.cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data
        ) {
          this.setStateAsync({
            policyStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS,
            progressMessage: res.data.message,
          });
        } else {
          this.setStateAsync({
            policyStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
            errorMessage: IntlUtil.getText(
              this._intl_ns_vpa_voicesetup,
              "notification.error.routePolicyCreateFailed"
            ),
          });
        }
      })
      .catch(async (err) => {
        const errorStatus = ResponseUtil.getStatus(err);
        if (
          errorStatus &&
          errorStatus === AppConfigProps.httpStatusCode.badRequest
        ) {
          const primeError = ResponseUtil.getPrimeError(err);
          if (
            primeError &&
            primeError.code ===
              VoiceAdminConstants.SERVER_ERROR_CODE.ROUTING_POLICY_CREATE_FAILED
          ) {
            await this.setStateAsync({
              errorDetails: primeError.message,
            });
          }
          this.setStateAsync({
            policyStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
            errorMessage: IntlUtil.getText(
              this._intl_ns_vpa_voicesetup,
              "notification.error.routePolicyCreateFailed"
            ),
          });
        } else {
          await this.setState({ isError: true });
          await manageError(err, this.props.history);
        }
      });
  };

  updateSubscriberAccount = async () => {
    this.setStateAsync({
      progressMessage: null,
      errorMessage: null,
      errorDetails: null,
    });
    try {
      this.setStateAsync({
        accountStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.IN_PROGRESS,
        progressMessage: IntlUtil.getText(
          this._intl_ns_vpa_voicesetup,
          "notification.info.completeSetUpProcess"
        ),
      });
      await this.props.setSubscriberIdentity();
      await this.props.setSubscriberAccount(this._cancelToken);
      await this.props.setSubscriberMenus(this.props.subscriberAccount);
      this.setStateAsync({
        accountStatus: VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS,
        progressMessage: null,
        errorMessage: null,
        errorDetails: null,
      });
    } catch (err) {
      await this.setState({ isError: true });
      await manageError(err, this.props.history);
    }
  };

  loadNextPage = async () => {
    await this.props.setVoiceSetupProgressPage(
      VoiceAdminConstants.VOICE_SETUP.PROGRESS.SUMMARY
    );
  };

  loadPreviousPage = async () => {
    await this.props.setVoiceSetupProgressPage(
      VoiceAdminConstants.VOICE_SETUP.PROGRESS.PRESENCE
    );
  };

  consentLinkClick = async () => {
    await openConsentPopup();
  };

  render() {
    let domainArrow = classNames(
      "vpa-voice-setup-item-arrow-wait",
      this.props.className,
      {
        "vpa-voice-setup-item-arrow-wait":
          this.state.domainStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.NOT_STARTED,
        "vpa-voice-setup-item-arrow-success":
          this.state.domainStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS,
        "vpa-voice-setup-item-arrow-failure":
          this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
      }
    );
    let pstnUsageArrow = classNames(
      "vpa-voice-setup-item-arrow-wait",
      this.props.className,
      {
        "vpa-voice-setup-item-arrow-wait":
          this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.NOT_STARTED,
        "vpa-voice-setup-item-arrow-success":
          this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS,
        "vpa-voice-setup-item-arrow-failure":
          this.state.routeStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
      }
    );
    let voiceRouteArrow = classNames(
      "vpa-voice-setup-item-arrow-wait",
      this.props.className,
      {
        "vpa-voice-setup-item-arrow-wait":
          this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.NOT_STARTED,
        "vpa-voice-setup-item-arrow-success":
          this.state.routeStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.SUCCESS,
        "vpa-voice-setup-item-arrow-failure":
          this.state.policyStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE,
      }
    );
    return (
      <Grid
        fluid
        className={`${
          this.props.isRoutingEnablePage
            ? "page-grid m-t-15 p-0"
            : "page-grid m-t-15"
        }`}
      >
        <Row>
          <Col xs={12} className="m-b-20">
            <Text className="page-heading4">
              {IntlUtil.getText(
                this._intl_ns_vpa_voicesetup,
                "content.setup.setupRoutingPolicies"
              )}
            </Text>
          </Col>
        </Row>
        <Row>
          <Col
            xs={12}
            sm={12}
            md={12}
            lg={this.props.isRoutingEnablePage ? 12 : 8}
          >
            <Flex column>
              <Row>
                <Col xs={12} className="m-b-20 m-t-30">
                  <Flex gap="gap.medium" vAlign="start">
                    <SetupItem
                      title={IntlUtil.getText(
                        this._intl_ns_vpa_voicesetup,
                        "content.setup.domain"
                      )}
                      status={this.state.domainStatus}
                    />
                    <ArrowRightIcon size="larger" className={domainArrow} />
                    <SetupItem
                      title={IntlUtil.getText(
                        this._intl_ns_vpa_voicesetup,
                        "content.setup.pstnUsage"
                      )}
                      status={this.state.pstnStatus}
                    />
                    <ArrowRightIcon size="larger" className={pstnUsageArrow} />
                    <SetupItem
                      title={IntlUtil.getText(
                        this._intl_ns_vpa_voicesetup,
                        "content.setup.voiceRoute"
                      )}
                      status={this.state.routeStatus}
                    />
                    <ArrowRightIcon size="larger" className={voiceRouteArrow} />
                    <SetupItem
                      title={IntlUtil.getText(
                        this._intl_ns_vpa_voicesetup,
                        "content.setup.routePolicy"
                      )}
                      status={this.state.policyStatus}
                    />
                  </Flex>
                </Col>
              </Row>

              {this.state.errorMessage !== null ? (
                <Row>
                  <Col xs={12} className="m-t-20 m-b-20">
                    <AlertErrorMessage
                      content={this.state.errorMessage}
                      moreDetails={{
                        label: IntlUtil.getText(
                          this._intl_ns_vpa_voicesetup,
                          "content.moreDetails"
                        ),
                        content: this.state.errorDetails,
                      }}
                    />
                    {this.state.domainStatus ===
                      VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE &&
                    this.state.errorDetails ? (
                      <div className="m-15">
                        <span>
                          {IntlUtil.getText(
                            this._intl_ns_vpa_voicesetup,
                            "content.consentMessage1"
                          )}
                        </span>
                        <span
                          className="text-link"
                          onClick={this.consentLinkClick}
                        >
                          {IntlUtil.getText(
                            this._intl_ns_vpa_voicesetup,
                            "content.acceptRevisedConsent"
                          )}
                        </span>
                        <span>
                          {IntlUtil.getText(
                            this._intl_ns_vpa_voicesetup,
                            "content.consentMessage2"
                          )}
                        </span>
                      </div>
                    ) : null}
                  </Col>
                </Row>
              ) : this.state.progressMessage !== null ? (
                <Row>
                  <Col xs={12} className="m-t-20 m-b-20">
                    <Text className="page-text">
                      {this.state.progressMessage}
                    </Text>
                  </Col>
                </Row>
              ) : null}
            </Flex>
          </Col>
        </Row>

        {this.state.domainStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE ||
        this.state.pstnStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE ||
        this.state.routeStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE ||
        this.state.policyStatus ===
          VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.FAILURE ? (
          <>
            <Row>
              <Col
                className={`m-t-20 ${
                  this.props.isRoutingEnablePage ? "" : "m-b-20"
                }`}
              >
                <Button
                  primary
                  content={IntlUtil.getText(
                    this._intl_ns_vpa_voicesetup,
                    "content.tryAgain"
                  )}
                  onClick={this.loadPageData}
                  className="vpa-voice-setup-button m-l-5 m-r-20"
                />
              </Col>
              {this.props.isRoutingEnablePage ? (
                <Col className="m-t-20">
                  <Button
                    primary
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_voicesetup,
                      "content.close"
                    )}
                    disabled={this.props.closeButtonDisabled}
                    onClick={this.props.closeButtonOnClick}
                    className="vpa-voice-setup-button m-l-5 m-r-20"
                  />
                </Col>
              ) : (
                <Col className="m-t-20">
                  <Button
                    primary
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_voicesetup,
                      "content.goBack"
                    )}
                    onClick={this.loadPreviousPage}
                    className="vpa-voice-setup-button m-l-5 m-r-20"
                  />
                </Col>
              )}
            </Row>
          </>
        ) : (
          <Row>
            <Col
              xs={12}
              className={`m-t-20 ${
                this.props.isRoutingEnablePage ? "" : "m-b-20"
              }`}
            >
              {this.props.isRoutingEnablePage ? (
                <Button
                  primary
                  content={IntlUtil.getText(
                    this._intl_ns_vpa_voicesetup,
                    "content.close"
                  )}
                  disabled={this.props.closeButtonDisabled}
                  onClick={this.props.closeButtonOnClick}
                  className="vpa-voice-setup-button"
                />
              ) : (
                <Button
                  primary
                  content={IntlUtil.getText(
                    this._intl_ns_vpa_voicesetup,
                    "content.continueSetup"
                  )}
                  disabled={
                    this.state.domainStatus ===
                      VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS
                        .IN_PROGRESS ||
                    this.state.pstnStatus ===
                      VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS
                        .IN_PROGRESS ||
                    this.state.routeStatus ===
                      VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS
                        .IN_PROGRESS ||
                    this.state.policyStatus ===
                      VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS
                        .IN_PROGRESS ||
                    this.state.accountStatus ===
                      VoiceAdminConstants.VOICE_SETUP.SETUP_STATUS.IN_PROGRESS
                  }
                  onClick={this.loadNextPage}
                  className="vpa-voice-setup-button"
                />
              )}
              <PageExitGuard
                when={this.state.isError === false}
                navigate={(path) => this.props.history.push(path)}
                shouldBlockNavigation={(location) => {
                  if (location.pathname) {
                    return true;
                  }

                  return false;
                }}
              />
            </Col>
          </Row>
        )}
      </Grid>
    );
  }
}

const mapStateToProps = (state) => ({
  voiceSetupProgressPage: state.voiceSetupStore.voiceSetupProgressPage,
  voiceSetupProgressVoiceRegions:
    state.voiceSetupStore.voiceSetupProgressVoiceRegions,
  subscriberAccount: state.subscriberStore.subscriberAccount,
});

const mapActionToProps = {
  setVoiceSetupProgressPage,
  setSubscriberIdentity,
  setSubscriberAccount,
  setSubscriberMenus,
};

export default connect(mapStateToProps, mapActionToProps)(Setup);
